import { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { marked } from 'marked';
import type { LegalDocument } from '@/legal';

interface Props {
  document: LegalDocument | null;
  onClose: () => void;
}

// Настройка marked один раз
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Модалка для чтения юридических документов
 * Использует marked для надёжного парсинга markdown
 */
export function LegalDocumentViewer({ document: doc, onClose }: Props) {
  // Парсим markdown в HTML — мемоизация чтобы не делать на каждый рендер
  const html = useMemo(() => {
    if (!doc) return '';
    try {
      return marked.parse(doc.content) as string;
    } catch (e) {
      console.error('[LegalDocumentViewer] markdown parse error:', e);
      return '<p>Не удалось загрузить документ. Попробуй ещё раз.</p>';
    }
  }, [doc]);

  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    if (doc) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [doc]);

  if (!doc) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="bg-bg w-full max-w-md h-[92vh] sm:h-[85vh] sm:rounded-2xl rounded-t-3xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-line shrink-0">
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm truncate">{doc.title}</div>
              <div className="text-[11px] text-ink-dim mt-0.5">Действует с 07.06.2026</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-bg-elevated flex items-center justify-center text-ink-muted hover:text-ink active:scale-95 transition-all shrink-0 ml-3"
              aria-label="Закрыть"
            >
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div 
              className="text-sm text-ink-muted leading-relaxed [&_h1]:font-display [&_h1]:font-bold [&_h1]:text-xl [&_h1]:text-ink [&_h1]:mt-6 [&_h1]:mb-3 [&_h1:first-child]:mt-0 [&_h2]:font-display [&_h2]:font-bold [&_h2]:text-lg [&_h2]:text-ink [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:font-display [&_h3]:font-semibold [&_h3]:text-base [&_h3]:text-ink [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:leading-relaxed [&_strong]:text-ink [&_strong]:font-semibold [&_em]:italic [&_a]:text-emerald-glow [&_a]:underline [&_a]:break-all [&_hr]:border-line [&_hr]:my-4 [&_table]:w-full [&_table]:text-xs [&_table]:my-3 [&_table]:border-collapse [&_th]:font-semibold [&_th]:text-ink [&_th]:border [&_th]:border-line [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:bg-bg-elevated [&_td]:border [&_td]:border-line [&_td]:px-2 [&_td]:py-1.5 [&_td]:align-top [&_blockquote]:border-l-4 [&_blockquote]:border-emerald-glow/50 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:my-3 [&_code]:bg-bg-elevated [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-line shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-display font-bold text-sm active:scale-[0.98] transition-all"
            >
              Понятно
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
